import { useEffect, useState } from "react";
import { EnterPhoneNumber } from "./enter-phone-number";
import { LoginImageContainer } from "./image-container";
import { useAppDispatch, useAppSelector } from "../../reducers/store";
import { useNavigate } from "react-router-dom";
import { EnterOTP } from "./enter-otp";
import { ForgotPassword } from "./forgot-password";
import { useToaster } from "../../components/hooks/useToaster";
import {
  loginUser,
  logoutUser,
  verifyOTP,
  sendOTP as sendOTPAction,
  sendOTPByEmail,
  verifyOTPByEmail,
} from "../../reducers/sessionSlice";

export function Login() {
  const navigate = useNavigate();
  const [toasterState, openToaster, closeToaster] = useToaster();
  const dispatch = useAppDispatch();

  const { profile, authToken, loading, error } = useAppSelector(
    (state) => state.session,
  );

  const [areaCode, setAreaCode] = useState("+1");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [enterCode, setEnterCode] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [forgotPassword, setForgotPassword] = useState(false);
  const [usingEmail, setUsingEmail] = useState(false);
  const [isVeifyButtonDisabled, setIsVerifyButtonDisabled] = useState(false);

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem("auth_token");
    const script: HTMLScriptElement = document.createElement("script");

    script.src = "//js.hs-scripts.com/7245989.js";
    script.async = true;
  
    document.body.appendChild(script);

    // Check if logging in as a customer via URL hash
    if (window.location.hash) {
      localStorage.clear();
      let qs = window.location.hash.split("+").join(" ");
      let params: any = {};
      const re = /[#&]?([^=]+)=([^&]*)/g;
      let tokens;
      while ((tokens = re.exec(qs))) {
        params[decodeURIComponent(tokens[1])] = decodeURIComponent(tokens[2]);
      }
      if (params.token && params.email) {
        localStorage.setItem("auth_token", params.token);
        localStorage.setItem("userEmail", params.email);
        setTimeout(() => {
          navigate("/home");
        }, 500);
        return; // Exit early to prevent immediate navigation
      }
    }

    // If token exists, navigate to home
    if (token) {
      navigate("/home");
    }
  }, []);

  // Watch for successful login/verification
  useEffect(() => {
    if (authToken) {
      navigate("/home");
    }
  }, [authToken, navigate]);
  

  // Login function using Redux
  const loginWithEmail = async (): Promise<void> => {
    const result = await dispatch(loginUser({ email, password }));
    if (result.type.endsWith('/rejected')) {
      openToaster(
        "Email or password is incorrect. Please try again.",
        "medium",
        "error",
      );
    }
  };

  const checkOTP = async (): Promise<void> => {
  try {
    setIsVerifyButtonDisabled(true);
    if (usingEmail) {
      await dispatch(
        verifyOTPByEmail({
          email,
          verificationCode,
        }),
      ).unwrap();
    } else {
      await dispatch(
        verifyOTP({
          phoneNumber: `${areaCode}${phoneNumber}`,
          verificationCode,
        }),
      ).unwrap();
      setIsVerifyButtonDisabled(false);
    }
    navigate("/home");
  } catch (error) {
    openToaster(
      "OTP code is incorrect. Please try again.",
      "medium",
      "error",
    );
  }
  setIsVerifyButtonDisabled(false);
};

  const sendOTP = async (): Promise<void> => {
    const result = await dispatch(
      sendOTPAction({
        phoneNumber: `${areaCode}${phoneNumber}`,
        email: "", // Required by type but not used for phone OTP
      }),
    );
    if (result.type.endsWith('/rejected')) {
      openToaster(
        "No user found with the provided phone number",
        "medium",
        "error",
      );
    } else {
      setEnterCode(true);
    }
  };

    // send otp to email
    const sendOTPEmail = async (): Promise<void> => {
      const result = await dispatch(
        sendOTPByEmail({
          email: email,
          phoneNumber: "", // Required by type but not used for email OTP
        }),
      );
      if (result.type.endsWith('/rejected')) {
        openToaster(
          "No user found with the provided email",
          "medium",
          "error",
        );
      } else {
        setEnterCode(true);
        setUsingEmail(true);
        setForgotPassword(false);
      }
    };
  return (
    <div className="flex h-screen w-screen items-center justify-center gap-x-[28px] pb-[5rem] pl-[2rem] pr-[2rem] pt-[5rem] max-[680px]:gap-x-0 max-[680px]:pb-[15rem] max-[680px]:pt-[0rem]">
      <div className="max-w-[800px] flex-grow max-[1200px]:hidden">
        <LoginImageContainer />
      </div>
      {!enterCode && !forgotPassword && (
        <div className="aspect-square max-w-[800px] flex-grow">
          <EnterPhoneNumber
            setAreaCode={setAreaCode}
            setPhoneNumber={setPhoneNumber}
            email={email}
            password={password}
            phoneNumber={phoneNumber}
            setEnterCode={setEnterCode}
            setPassword={setPassword}
            setEmail={setEmail}
            emailLogin={loginWithEmail}
            sendOTP={sendOTP}
            setForgotPassword={setForgotPassword}
          />
        </div>
      )}
      {enterCode && !forgotPassword && (
        <div className="max-w-[800px] flex-grow">
          <EnterOTP
            setVerificationCode={setVerificationCode}
            verificationCode={verificationCode}
            areaCode={areaCode}
            phoneNumber={phoneNumber}
            setEnterCode={setEnterCode}
            checkOTP={checkOTP}
            sendOTP={sendOTP}
            setAreaCode={setAreaCode}
            setPhoneNumber={setPhoneNumber}
            email={email}
            isUsingEmail={usingEmail}
            sendOTPEmail={sendOTPEmail}
            isVerifyButtonDisabled={isVeifyButtonDisabled}
          />
        </div>
      )}
      {forgotPassword && (
        <div className="max-w-[800px] flex-grow">
          <ForgotPassword
            setEnterCode={setEnterCode}
            checkOTP={checkOTP}
            sendOTP={sendOTPEmail}
            setAreaCode={setAreaCode}
            setEmail={setEmail}
            setForgotPassword={setForgotPassword}
            setUsingEmail={setUsingEmail}
            email={email}
          />
        </div>
      )}
    </div>
  );
}
